import React, { useState } from 'react';
import {
  Box,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Inventory as StockIcon,
} from '@mui/icons-material';

interface BulkActionsToolbarProps {
  selectedCount: number;
  onClear: () => void;
  onBulkDelete?: () => void;
  onBulkUpdateStock?: (quantity: number) => void;
  onBulkUpdateStatus?: (status: string) => void;
  statusOptions?: Array<{ value: string; label: string }>;
  inventoryType: 'spare-part' | 'operating-supply' | 'tool';
}

export const BulkActionsToolbar: React.FC<BulkActionsToolbarProps> = ({
  selectedCount,
  onClear,
  onBulkDelete,
  onBulkUpdateStock,
  onBulkUpdateStatus,
  statusOptions = [],
  inventoryType,
}) => {
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stockValue, setStockValue] = useState<string>('');
  const [statusValue, setStatusValue] = useState<string>('');

  const handleStockUpdate = () => {
    const quantity = parseInt(stockValue, 10);
    if (!isNaN(quantity) && quantity >= 0 && onBulkUpdateStock) {
      onBulkUpdateStock(quantity);
      setStockDialogOpen(false);
      setStockValue('');
    }
  };

  const handleStatusUpdate = () => {
    if (statusValue && onBulkUpdateStatus) {
      onBulkUpdateStatus(statusValue);
      setStatusDialogOpen(false);
      setStatusValue('');
    }
  };

  const handleDelete = () => {
    if (onBulkDelete) {
      onBulkDelete();
      setDeleteDialogOpen(false);
    }
  };

  const getTypeLabel = () => {
    switch (inventoryType) {
      case 'spare-part':
        return 'Ersatzteile';
      case 'operating-supply':
        return 'Betriebsmittel';
      case 'tool':
        return 'Werkzeuge';
      default:
        return 'Elemente';
    }
  };

  return (
    <>
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          bgcolor: 'primary.light',
          color: 'primary.contrastText',
        }}
      >
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {selectedCount} {selectedCount === 1 ? 'Element' : 'Elemente'} ausgewählt
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {onBulkUpdateStock && inventoryType !== 'tool' && (
            <Tooltip title="Bestand aktualisieren">
              <IconButton
                color="inherit"
                onClick={() => setStockDialogOpen(true)}
              >
                <StockIcon />
              </IconButton>
            </Tooltip>
          )}

          {onBulkUpdateStatus && statusOptions.length > 0 && (
            <Tooltip title="Status ändern">
              <IconButton
                color="inherit"
                onClick={() => setStatusDialogOpen(true)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          {onBulkDelete && (
            <Tooltip title="Löschen">
              <IconButton
                color="inherit"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Auswahl aufheben">
            <IconButton color="inherit" onClick={onClear}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      {/* Stock Update Dialog */}
      <Dialog
        open={stockDialogOpen}
        onClose={() => setStockDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Bestand aktualisieren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Neuer Bestand für {selectedCount} {getTypeLabel()}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Menge"
            type="number"
            fullWidth
            variant="outlined"
            value={stockValue}
            onChange={(e) => setStockValue(e.target.value)}
            InputProps={{ inputProps: { min: 0 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleStockUpdate}
            variant="contained"
            disabled={!stockValue || parseInt(stockValue, 10) < 0}
          >
            Aktualisieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Status ändern</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Neuer Status für {selectedCount} {getTypeLabel()}
          </Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusValue}
              label="Status"
              onChange={(e: SelectChangeEvent) => setStatusValue(e.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleStatusUpdate}
            variant="contained"
            disabled={!statusValue}
          >
            Aktualisieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Löschen bestätigen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie wirklich {selectedCount} {getTypeLabel()} löschen?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
